import './EditSkaiPacvueIdsModal.scss';
import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  AmazonAccountRow,
  SkaiPacvueAccountOption,
  SkaiPacvueSource
} from '@blisspointmedia/bpm-types/dist/AmazonSPAPI';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { TableRow } from '@Components/ModalEditWidget/modal-edit-constants';

const SOURCE_LABELS: Record<SkaiPacvueSource, string> = {
  skai: 'Skai',
  pacvue: 'Pacvue'
};

const skaiPacvueAccountLabel = (option: SkaiPacvueAccountOption): string => {
  const name = option.account_name ? option.account_name : option.account_id;
  const id = option.account_name ? ` (${option.account_id})` : '';
  return `${SOURCE_LABELS[option.source]}: ${name}${id}`;
};

interface EditSkaiPacvueIdsModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (updated: AmazonAccountRow) => void;
  row: TableRow<AmazonAccountRow>;
  options: SkaiPacvueAccountOption[];
  optionsLoading: boolean;
}

export const EditSkaiPacvueIdsModal = ({
  show,
  onClose,
  onSave,
  row,
  options,
  optionsLoading
}: EditSkaiPacvueIdsModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(row.skai_pacvue_account_ids);

  const dropdownOptions = useMemo(
    () =>
      options.map(option => ({ value: option.account_id, label: skaiPacvueAccountLabel(option) })),
    [options]
  );

  const handleClose = useCallback(() => {
    setSelectedIds(row.skai_pacvue_account_ids);
    onClose();
  }, [onClose, row.skai_pacvue_account_ids]);

  // Disable Save when the mapping is unchanged so an unedited row can't be
  // staged as a (no-op) pending change.
  const isUnchanged = useMemo(() => {
    const before = new Set(row.skai_pacvue_account_ids);
    return before.size === selectedIds.length && selectedIds.every(id => before.has(id));
  }, [row.skai_pacvue_account_ids, selectedIds]);

  const handleSave = useCallback(() => {
    // onSave is synchronous (stages changes); save errors surface via the parent's error dialog
    onSave({ ...row, skai_pacvue_account_ids: selectedIds });
    onClose();
  }, [onClose, onSave, row, selectedIds]);

  return (
    <Modal show={show} onHide={handleClose} className='edit-skai-pacvue-modal' centered>
      <Modal.Header closeButton>
        <div className='edit-skai-pacvue-title-container'>
          <h2 className='edit-skai-pacvue-title'>
            Map Skai / Pacvue IDs to {row.account_name || row.account_id}
          </h2>
          <p className='edit-skai-pacvue-subtitle'>Amazon Account ID: {row.account_id}</p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Dropdown
          size={Size.LG}
          searchable
          multiSelect
          showChips
          options={dropdownOptions}
          value={selectedIds}
          onChange={setSelectedIds}
          placeholder='Select Skai / Pacvue accounts'
          disabled={optionsLoading}
        />
        {/* raw <button> intentional: needs a custom link-style appearance not in Button component */}
        {selectedIds.length > 0 && (
          <button
            type='button'
            className='skai-pacvue-modal-clear'
            onClick={() => setSelectedIds([])}
          >
            Clear
          </button>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleClose}>
          Cancel
        </Button>
        <Button disabled={isUnchanged} onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
